import React, { useState } from 'react';
import theme from './BuyerChangeRequestOffer.module.scss';
import { Trans } from '@lingui/react/macro';
import { ChangeRequestProgressInfoOffer, BuyerOfferStatus } from '@/types';
import { DateTimeDisplay } from '@/components/localization';
import { InternalLinkButton, Button } from '@/components/Buttons';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { BuyerOfferStatusIndicator } from '@/components/StatusIndicator';
import BuyerChangeRequestOfferGroupList from './BuyerChangeRequestOfferGroupList';
import BuyerChangeRequestOfferRejectDialog from './BuyerChangeRequestOfferRejectDialog';
import ProfileAndUserDisplay from '@/components/ProfileAndUserDisplay';
import { ExpandableCard, CardSection, ExpandableCardHandle } from '@/components/Cards';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';

interface BuyerChangeRequestOfferProps {
    changeRequestId: number;
    offer: ChangeRequestProgressInfoOffer;
    onChange?(): void;
    isPreview?: boolean;
}

const BuyerChangeRequestOffer: React.FC<BuyerChangeRequestOfferProps> = ({
    offer,
    onChange,
    changeRequestId,
    isPreview,
}) => {
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const permissions = useSelector((state: IState) => state.buyer.permissions!);
    const [expanded, setExpanded] = useState(
        offer.status === BuyerOfferStatus.Pending || offer.status === BuyerOfferStatus.Accepted
    );

    const handleReject = async () => {
        triggerHandler(onChange);
    };

    const renderButtons = () => {
        if (!onChange) {
            return null;
        }

        if (offer.status === BuyerOfferStatus.Pending) {
            return (
                <div className={theme.buttons}>
                    <Button
                        disabled={isPreview || !permissions.canModify}
                        onClick={(e) => setShowRejectDialog(true)}
                        color="danger">
                        <Trans>Reject</Trans>
                    </Button>

                    <InternalLinkButton
                        to={`/change-requests/${changeRequestId}/offers/${offer.id}/submit`}
                        color="primary"
                        disabled={isPreview || !permissions.canModify}>
                        <Trans>Go to sign and send</Trans>
                    </InternalLinkButton>

                    {!isPreview && (
                        <BuyerChangeRequestOfferRejectDialog
                            offer={offer}
                            residentialUnitId={permissions.residentialUnitId}
                            show={showRejectDialog}
                            onClose={() => setShowRejectDialog(false)}
                            onReject={handleReject}
                        />
                    )}
                </div>
            );
        }

        return null;
        //  return <BuyerOfferStatusBadge value={offer.status} />;
    };

    const renderContent = () => {
        return (
            <div className={theme.container}>
                {/* <div className={theme.header}>
                    <ProfileAndUserDisplay profileId={offer.sentByProfile.id} profileName={offer.sentByProfile.name} />
                    <span className={theme.sendDate}>
                        <DateTimeDisplay value={offer.sendDate} />
                    </span>
                </div> */}
                <h6>
                    {isPreview ? <Trans>Offer</Trans> : <Trans>Offer #{offer.id}</Trans>}
                    <span className={theme.sendDate}>
                        <DateTimeDisplay value={offer.sendDate} />
                    </span>
                </h6>

                <div className={theme.note}>
                    {offer.note || (
                        <span className="text-missing">
                            <Trans>No description</Trans>
                        </span>
                    )}
                </div>

                <BuyerChangeRequestOfferGroupList groups={offer.groups} isPreview={isPreview} />

                <div className={theme.price}>
                    <Trans>Total price</Trans>
                    <span>
                        <BuyerCurrencyDisplay value={offer.totalPrice} />
                    </span>
                </div>

                {renderButtons()}
            </div>
        );
    };

    return (
        <ExpandableCard  className={theme.offerCard} expanded={expanded} expandableContent={renderContent()} type="inside-bordered">
            <CardSection>
                <ProfileAndUserDisplay
                    profileId={offer.sentByProfile.id}
                    profileName={offer.sentByProfile.name}
                    userId={offer.sentByUser.id}
                    userName={offer.sentByUser.name}
                />
            </CardSection>
            {!isPreview && (
                <CardSection caption={<Trans>Offer ID</Trans>} right width="100px">
                    #{offer.id}
                </CardSection>
            )}
            <CardSection caption={<Trans>Offer Status</Trans>} right width="120px">
                <BuyerOfferStatusIndicator value={offer.status} />
            </CardSection>
            <CardSection caption={<Trans>Total</Trans>} right className="text-right" width="100px">
                <BuyerCurrencyDisplay value={offer.totalPrice} />
            </CardSection>
            <ExpandableCardHandle onExpandToggle={setExpanded} expanded={expanded} />
        </ExpandableCard>
    );
};

export default BuyerChangeRequestOffer;
